import React, { useCallback, useEffect, useRef, useState } from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { StorageConfigurationService } from "@application/useCases/workspace/StorageConfigurationService";
import { PathTypeSchema } from "@domain/schemas/BI";
import { UploadDestination, UploadDestinationPickerDialog } from "./UploadDestinationPickerDialog";
import { getUploadErrorMessage } from "@presentation/common/ErrorHelpers";
import { useFastaFilesLocationName, useRawFilesLocationName } from "@presentation/hooks/useStorageLocations";

interface DestinationChoiceState {
  label: string;
  roots: string[];
  initialDestination?: Partial<UploadDestination>;
  resolve: (destination: UploadDestination | null) => void;
}

/**
Resolves just the destination-picker step (root + subfolder) for a facet - shared by single-file
flows (`useUploadDestinationFlow`, which layers a per-file overwrite-confirmation step on top) and
batch flows (Raw's multi-file upload dialog, which has its own batch conflict UI and only needs the
destination chosen once for the whole batch, not re-asked per file).
*/
export function useUploadDestinationPicker(pathType: PathTypeSchema, projectId: string | null | undefined, initialDestination?: Partial<UploadDestination>) {
  const storageConfigurationService = useInjection(StorageConfigurationService);
  const rawLocationName = useRawFilesLocationName();
  const fastaLocationName = useFastaFilesLocationName();
  const locationName = pathType === PathTypeSchema.Raw || pathType === PathTypeSchema.Libraries ? rawLocationName : fastaLocationName;
  const [destinationChoiceState, setDestinationChoiceState] = useState<DestinationChoiceState | null>(null);
  const [destination, setDestination] = useState<UploadDestination | null>(null);
  const [error, setError] = useState("");
  const [isChoosing, setIsChoosing] = useState(false);
  const pendingChoice = useRef<((destination: UploadDestination | null) => void) | null>(null);
  const generation = useRef(0);
  useEffect(() => {
    setDestination(null);
    setError("");
    setIsChoosing(false);
    setDestinationChoiceState(null);
    return () => {
      generation.current++;
      pendingChoice.current?.(null);
      pendingChoice.current = null;
    };
  }, [projectId, pathType]);

  useEffect(() => {
    if (!projectId || !initialDestination) return;
    let isCancelled = false;

    const selectInitialDestination = async () => {
      try {
        const effectiveConfig = await storageConfigurationService.getProjectEffectiveStorageConfigurationAsync(projectId);
        if (isCancelled) return;

        const roots = [...new Set((effectiveConfig.paths?.[pathType] ?? []).map((entry) => entry.path?.trim()).filter((path): path is string => Boolean(path)))];
        const initialRoot = initialDestination.rootPath?.trim();
        if (initialRoot && roots.includes(initialRoot)) {
          setDestination({ rootPath: initialRoot, folder: initialDestination.folder ?? "" });
          return;
        }

        if (initialRoot || roots.length > 1) return;

        const rootPath = roots[0] ?? "";
        let folder = initialDestination.folder ?? "";
        const rootPrefix = rootPath ? `${rootPath}/` : "";
        if (folder === rootPath) {
          folder = "";
        } else if (rootPrefix && folder.startsWith(rootPrefix)) {
          folder = folder.slice(rootPrefix.length);
        }
        setDestination({ rootPath, folder });
      } catch (error) {
        if (!isCancelled) setError(getUploadErrorMessage(error));
      }
    };

    void selectInitialDestination();
    return () => {
      isCancelled = true;
    };
  }, [initialDestination, pathType, projectId, storageConfigurationService]);

  const listFolders = useCallback((type: PathTypeSchema, folder: string) => storageConfigurationService.listFoldersAsync(type, folder), [storageConfigurationService]);

  const pickDestination = useCallback(
    async (label: string): Promise<UploadDestination | null> => {
      const currentGeneration = generation.current;
      setError("");
      setIsChoosing(true);
      try {
        if (!projectId) throw new Error("Select a project before choosing an upload directory.");
        const effectiveConfig = await storageConfigurationService.getProjectEffectiveStorageConfigurationAsync(projectId);
        if (generation.current !== currentGeneration) return null;
        const roots = [...new Set((effectiveConfig.paths?.[pathType] ?? []).map((entry) => entry.path?.trim() ?? ""))];
        const choice = await new Promise<UploadDestination | null>((resolve) => {
          pendingChoice.current = resolve;
          setDestinationChoiceState({ label, roots, initialDestination: destination ?? initialDestination, resolve });
        });
        if (generation.current !== currentGeneration) return null;
        if (choice) setDestination(choice);
        return choice;
      } catch (error) {
        if (generation.current === currentGeneration) setError(getUploadErrorMessage(error));
        return null;
      } finally {
        if (generation.current === currentGeneration) {
          pendingChoice.current = null;
          setDestinationChoiceState(null);
          setIsChoosing(false);
        }
      }
    },
    [projectId, pathType, storageConfigurationService, initialDestination, destination],
  );

  const dialog = destinationChoiceState && (
    <UploadDestinationPickerDialog
      fileName={destinationChoiceState.label}
      pathType={pathType}
      locationName={locationName}
      roots={destinationChoiceState.roots}
      initialDestination={destinationChoiceState.initialDestination}
      listFoldersAsync={listFolders}
      onContinue={(destination) => destinationChoiceState.resolve(destination)}
      onCancel={() => destinationChoiceState.resolve(null)}
    />
  );

  return { dialog, pickDestination, destination, error, isChoosing };
}
